<template>
  <div class="employee-page">
    <HeaderBar />

    <main class="content">
      <el-card class="step-card" shadow="hover">
        <div class="step-copy">
          <p class="step-label">Step 1 of 3</p>
          <h1>Background verification</h1>
          <p class="step-text">
            Take a live selfie with your camera and upload your identification document to start employee onboarding.
          </p>
        </div>

        <el-alert
          class="status-alert"
          type="info"
          :closable="false"
          title="A live camera selfie and an identification document are required before you can continue."
          :description="`Maximum file size: ${maxFileSizeLabel} for the identification document.`"
        />

        <el-form label-position="top" class="verification-form" @submit.prevent>
          <el-form-item label="Selfie photo" :error="errors.selfie">
            <div class="upload-panel">
              <div class="upload-copy">
                <strong>Take your selfie using the camera</strong>
                <p>The portal uses your device camera directly. File upload is not used for the selfie step.</p>
              </div>

              <div class="camera-frame" :class="{ active: cameraActive, captured: !!selfiePreviewUrl && !cameraActive }">
                <video
                  v-show="cameraActive"
                  ref="selfieVideo"
                  class="camera-video"
                  autoplay
                  playsinline
                  muted
                />
                <img
                  v-if="selfiePreviewUrl && !cameraActive"
                  :src="selfiePreviewUrl"
                  alt="Captured selfie preview"
                  class="captured-image"
                >
                <div v-if="!cameraActive && !selfiePreviewUrl" class="camera-placeholder">
                  Camera preview will appear here after permission is granted.
                </div>
              </div>

              <p v-if="cameraError" class="camera-error">{{ cameraError }}</p>

              <div class="upload-actions">
                <el-button
                  v-if="!cameraActive && !selfieFile"
                  type="primary"
                  plain
                  :loading="cameraStarting"
                  @click="startCamera()"
                >
                  Open camera
                </el-button>
                <el-button
                  v-if="cameraActive"
                  type="success"
                  :disabled="cameraStarting"
                  @click="captureSelfie"
                >
                  Take selfie
                </el-button>
                <el-button
                  v-if="selfieFile && !cameraActive"
                  type="warning"
                  @click="retakeSelfie"
                >
                  Retake selfie
                </el-button>
              </div>

              <p v-if="selfieFile" class="file-meta">
                Selfie captured and ready for submission.
              </p>
            </div>
            <canvas ref="selfieCanvas" class="hidden-canvas" />
          </el-form-item>

          <el-form-item label="Identification document" :error="errors.idDocument">
            <input
              ref="documentInput"
              class="hidden-input"
              type="file"
              @change="onDocumentSelected"
            >

            <div class="upload-panel">
              <div class="upload-copy">
                <strong>Upload your identification document</strong>
                <p>Select the document file from your device. The portal does not restrict the file type.</p>
              </div>

              <div class="upload-actions">
                <el-button type="primary" plain @click="triggerInput('documentInput')">
                  Choose document
                </el-button>
                <el-button v-if="idDocumentFile" text @click="clearDocument">
                  Remove
                </el-button>
              </div>

              <p v-if="idDocumentFile" class="file-meta">
                {{ idDocumentFile.name }} ({{ formatFileSize(idDocumentFile.size) }})
              </p>
            </div>
          </el-form-item>

          <div class="actions">
            <el-button
              type="primary"
              size="large"
              :loading="submitting"
              :disabled="!canSubmit"
              @click="submitVerification"
            >
              Submit verification
            </el-button>
            <el-button size="large" @click="logout">
              Log out
            </el-button>
          </div>
        </el-form>
      </el-card>
    </main>
  </div>
</template>

<script>
import HeaderBar from '@/components/HeaderBar.vue'
import { auth, storage, serverTimestamp } from '@/firebase'
import {
  buildEmployeeVerificationStoragePath,
  EMPLOYEE_PORTAL_STATUSES,
  MAX_EMPLOYEE_UPLOAD_SIZE_BYTES,
  mergeUserProfile,
} from '@/services/userProfile'
import { ElMessage } from 'element-plus'
import { signOut } from 'firebase/auth'
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'

export default {
  name: 'EmployeeVerificationView',
  components: { HeaderBar },
  data() {
    return {
      selfieFile: null,
      idDocumentFile: null,
      selfiePreviewUrl: '',
      cameraStream: null,
      cameraStarting: false,
      cameraError: '',
      submitting: false,
      errors: {
        selfie: '',
        idDocument: '',
      },
      maxFileSizeBytes: MAX_EMPLOYEE_UPLOAD_SIZE_BYTES,
    }
  },
  computed: {
    canSubmit() {
      return !!this.selfieFile && !!this.idDocumentFile && !this.submitting
    },
    cameraActive() {
      return !!this.cameraStream
    },
    maxFileSizeLabel() {
      return this.formatFileSize(this.maxFileSizeBytes)
    }
  },
  mounted() {
    this.startCamera(true)
  },
  beforeUnmount() {
    this.stopCameraStream()
    this.revokeSelfiePreview()
  },
  methods: {
    triggerInput(refName) {
      const input = this.$refs[refName]
      if (input?.click) input.click()
    },
    resetInput(refName) {
      const input = this.$refs[refName]
      if (input) input.value = ''
    },
    revokeSelfiePreview() {
      if (!this.selfiePreviewUrl) return
      URL.revokeObjectURL(this.selfiePreviewUrl)
      this.selfiePreviewUrl = ''
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex += 1
      }
      const precision = unitIndex === 0 ? 0 : 1
      return `${size.toFixed(precision)} ${units[unitIndex]}`
    },
    validateSelfie(file) {
      if (!file) return 'Take a selfie with the camera before continuing.'
      if (!file.type || !file.type.startsWith('image/')) return 'The captured selfie image is invalid. Please retake it.'
      if (file.size > this.maxFileSizeBytes) return `Selfie photo must be ${this.maxFileSizeLabel} or smaller. Please retake it.`
      return ''
    },
    validateDocument(file) {
      if (!file) return 'An identification document is required.'
      if (file.size > this.maxFileSizeBytes) return `Identification document must be ${this.maxFileSizeLabel} or smaller.`
      return ''
    },
    setSelfieFile(file) {
      const error = this.validateSelfie(file)
      this.errors.selfie = error
      if (error) return false

      this.selfieFile = file
      this.revokeSelfiePreview()
      this.selfiePreviewUrl = URL.createObjectURL(file)
      this.cameraError = ''
      return true
    },
    setDocumentFile(file) {
      const error = this.validateDocument(file)
      this.errors.idDocument = error
      if (error) {
        this.resetInput('documentInput')
        return
      }
      this.idDocumentFile = file
    },
    describeCameraError(error) {
      const code = error?.name || error?.code || ''
      if (code === 'NotAllowedError' || code === 'PermissionDeniedError') {
        return 'Camera access was denied. Allow camera access in the browser and try again.'
      }
      if (code === 'NotFoundError' || code === 'DevicesNotFoundError') {
        return 'No camera was found on this device.'
      }
      if (code === 'NotReadableError' || code === 'TrackStartError') {
        return 'The camera is already in use by another application.'
      }
      if (code === 'OverconstrainedError' || code === 'ConstraintNotSatisfiedError') {
        return 'The preferred front camera is unavailable on this device.'
      }
      if (code === 'TypeError') {
        return 'This browser does not support the required camera APIs.'
      }
      return error?.message || 'Unable to access the camera right now.'
    },
    attachCameraStream(stream) {
      const video = this.$refs.selfieVideo
      if (!video) return
      video.srcObject = stream
      const playAttempt = video.play?.()
      if (playAttempt?.catch) {
        playAttempt.catch(() => {})
      }
    },
    stopCameraStream() {
      if (this.cameraStream?.getTracks) {
        this.cameraStream.getTracks().forEach((track) => track.stop())
      }
      this.cameraStream = null

      const video = this.$refs.selfieVideo
      if (video) video.srcObject = null
    },
    async requestCamera(constraints) {
      return navigator.mediaDevices.getUserMedia(constraints)
    },
    async startCamera(silent = false) {
      if (!navigator.mediaDevices?.getUserMedia) {
        this.cameraError = 'This browser does not support live camera capture.'
        this.errors.selfie = 'A camera-supported browser is required to take the selfie.'
        if (!silent) {
          ElMessage({ type: 'error', message: this.cameraError, showClose: true })
        }
        return
      }

      this.cameraStarting = true
      this.cameraError = ''
      this.errors.selfie = ''

      try {
        this.stopCameraStream()

        let stream = null
        try {
          stream = await this.requestCamera({
            video: { facingMode: { ideal: 'user' }, width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          })
        } catch (frontCameraError) {
          stream = await this.requestCamera({ video: true, audio: false })
        }

        this.cameraStream = stream
        await this.$nextTick()
        this.attachCameraStream(stream)
      } catch (error) {
        this.stopCameraStream()
        this.cameraError = this.describeCameraError(error)
        this.errors.selfie = 'Take a selfie with the camera before continuing.'
        if (!silent) {
          ElMessage({ type: 'error', message: this.cameraError, showClose: true, duration: 5000 })
        }
      } finally {
        this.cameraStarting = false
      }
    },
    async captureSelfie() {
      const video = this.$refs.selfieVideo
      const canvas = this.$refs.selfieCanvas

      if (!this.cameraStream || !video || !canvas) {
        this.errors.selfie = 'Open the camera before taking the selfie.'
        return
      }

      const width = video.videoWidth || 0
      const height = video.videoHeight || 0
      if (!width || !height) {
        this.errors.selfie = 'Camera preview is not ready yet. Please wait a moment and try again.'
        return
      }

      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      if (!context) {
        this.errors.selfie = 'Unable to process the selfie image.'
        return
      }

      context.drawImage(video, 0, 0, width, height)

      try {
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result) resolve(result)
            else reject(new Error('Unable to capture the selfie image.'))
          }, 'image/jpeg', 0.92)
        })

        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })

        const isValid = this.setSelfieFile(file)
        if (!isValid) {
          ElMessage({ type: 'error', message: this.errors.selfie, showClose: true })
          return
        }

        this.stopCameraStream()
        ElMessage({ type: 'success', message: 'Selfie captured successfully.', showClose: true })
      } catch (error) {
        this.errors.selfie = error?.message || 'Unable to capture the selfie image.'
        ElMessage({ type: 'error', message: this.errors.selfie, showClose: true, duration: 5000 })
      }
    },
    async retakeSelfie() {
      this.clearSelfie()
      await this.startCamera()
    },
    onDocumentSelected(event) {
      const file = event?.target?.files?.[0] || null
      if (!file) return
      this.setDocumentFile(file)
      if (this.errors.idDocument) {
        ElMessage({ type: 'error', message: this.errors.idDocument, showClose: true })
      }
    },
    clearSelfie() {
      this.selfieFile = null
      this.errors.selfie = ''
      this.cameraError = ''
      this.revokeSelfiePreview()
    },
    clearDocument() {
      this.idDocumentFile = null
      this.errors.idDocument = ''
      this.resetInput('documentInput')
    },
    async uploadVerificationFiles(user) {
      const selfiePath = buildEmployeeVerificationStoragePath(user.uid, 'selfie', this.selfieFile.name)
      const idDocumentPath = buildEmployeeVerificationStoragePath(user.uid, 'document', this.idDocumentFile.name)

      const selfieRef = storageRef(storage, selfiePath)
      const documentRef = storageRef(storage, idDocumentPath)

      await Promise.all([
        uploadBytes(selfieRef, this.selfieFile, { contentType: this.selfieFile.type || 'image/jpeg' }),
        uploadBytes(documentRef, this.idDocumentFile, { contentType: this.idDocumentFile.type || 'application/octet-stream' }),
      ])

      const [selfieUrl, idDocumentUrl] = await Promise.all([
        getDownloadURL(selfieRef),
        getDownloadURL(documentRef),
      ])

      return {
        selfieUrl,
        selfiePath,
        idDocumentUrl,
        idDocumentPath,
      }
    },
    validateForm() {
      this.errors.selfie = this.validateSelfie(this.selfieFile)
      this.errors.idDocument = this.validateDocument(this.idDocumentFile)
      return !this.errors.selfie && !this.errors.idDocument
    },
    async submitVerification() {
      if (this.submitting) return
      if (!this.validateForm()) {
        ElMessage({ type: 'error', message: 'Complete the selfie capture and upload the required document before continuing.', showClose: true })
        return
      }

      const user = auth.currentUser
      if (!user?.uid) {
        this.$router.push({ name: 'Login' })
        return
      }

      this.submitting = true
      try {
        const uploadResult = await this.uploadVerificationFiles(user)

        await mergeUserProfile(user, {
          employeePortal: {
            status: EMPLOYEE_PORTAL_STATUSES.FORM_REQUIRED,
            selfieUrl: uploadResult?.selfieUrl || '',
            selfiePath: uploadResult?.selfiePath || '',
            idDocumentUrl: uploadResult?.idDocumentUrl || '',
            idDocumentPath: uploadResult?.idDocumentPath || '',
            verificationSubmittedAt: serverTimestamp(),
            formCompletedAt: null,
            approvedAt: null,
            approvedBy: '',
            approvedByUid: '',
            approvedByEmail: '',
            denialComment: '',
            deniedAt: null,
            deniedBy: '',
            deniedByUid: '',
            deniedByEmail: '',
          }
        })

        ElMessage({ type: 'success', message: 'Verification files saved successfully.', showClose: true })
        this.$router.push({ name: 'EmployeeFormStep' })
      } catch (error) {
        const isStorageAuthError = error?.code === 'storage/unauthorized'
        ElMessage({
          type: 'error',
          message: isStorageAuthError
            ? 'Firebase Storage denied the upload. Update the live Storage rules to allow employee-verification/{uid}/... for the signed-in user, then try again.'
            : (error?.message || 'Unable to save your verification files right now.'),
          showClose: true,
          duration: 5000,
        })
      } finally {
        this.submitting = false
      }
    },
    async logout() {
      try {
        await signOut(auth)
      } catch (e) {}
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>

<style scoped>
.employee-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f6fbff 0%, #e9f2ff 100%);
  color: #03241f;
}

.content {
  max-width: 840px;
  margin: 36px auto 64px;
  padding: 0 16px;
  box-sizing: border-box;
}

.step-card {
  border-radius: 22px;
  border: 1px solid rgba(3, 36, 31, 0.1);
  box-shadow: 0 20px 48px rgba(3, 36, 31, 0.12);
}

.step-copy {
  margin-bottom: 20px;
}

.step-label {
  margin: 0 0 8px;
  color: #0b6cff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.step-copy h1 {
  margin: 0 0 8px;
  font-size: 2rem;
  font-weight: 800;
}

.step-text {
  margin: 0;
  color: #486a64;
  font-size: 1rem;
}

.status-alert {
  margin-bottom: 24px;
}

.verification-form {
  margin-top: 8px;
}

.hidden-input,
.hidden-canvas {
  display: none;
}

.upload-panel {
  width: 100%;
  border: 1px solid rgba(3, 36, 31, 0.14);
  border-radius: 18px;
  padding: 18px;
  box-sizing: border-box;
  background: #f9fcff;
}

.upload-copy strong {
  display: block;
  margin-bottom: 4px;
  color: #03241f;
}

.upload-copy p {
  margin: 0 0 14px;
  color: #486a64;
}

.camera-frame {
  width: min(100%, 420px);
  min-height: 260px;
  border-radius: 18px;
  overflow: hidden;
  margin-bottom: 14px;
  border: 1px solid rgba(3, 36, 31, 0.12);
  background: linear-gradient(180deg, #eef5ff 0%, #dfefff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.camera-frame.active {
  background: #041915;
}

.camera-video,
.captured-image {
  width: 100%;
  display: block;
  object-fit: cover;
}

.camera-video {
  min-height: 260px;
}

.captured-image {
  min-height: 260px;
}

.camera-placeholder {
  padding: 32px 24px;
  text-align: center;
  color: #486a64;
  font-weight: 600;
}

.camera-error {
  margin: 0 0 12px;
  color: #b42318;
  font-weight: 600;
}

.upload-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.file-meta {
  margin: 0;
  color: #0b6a59;
  font-weight: 600;
  word-break: break-word;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

@media (max-width: 600px) {
  .content {
    margin-top: 28px;
  }

  .step-copy h1 {
    font-size: 1.7rem;
  }

  .camera-frame,
  .camera-video,
  .captured-image {
    min-height: 220px;
  }

  .actions {
    flex-direction: column;
  }
}
</style>
